import React, { useState, useEffect, useRef } from 'react';
import { trackSuggestionsToggled } from '../../lib/analytics';
import { useGame } from '../../hooks/useGame';
import Grid from './Grid';
import SearchBox from './SearchBox';
import ScoreBar from './ScoreBar';
import ResultModal from './ResultModal';
import Toast from '../shared/Toast';
import SuggestAnswerModal from './SuggestAnswerModal';

export default function Game({ puzzle, testMode = false, showDifficulty = false }) {
  const game = useGame(puzzle, false, testMode);

  // All state declarations first
  const [showModal,      setShowModal]      = useState(false);
  const [showSuggest,    setShowSuggest]    = useState(false);
  const mountedGameOver = React.useRef(game.gameOver); // capture gameOver at mount time
  const focusSearchRef = useRef(null); // holds focus fn exposed by SearchBox

  // Show modal only when game transitions from active → over in this session
  // If gameOver was already true on mount (restored state), don't auto-show
  useEffect(() => {
    if (game.gameOver && !mountedGameOver.current) {
      setTimeout(() => setShowModal(true), 900);
    }
  }, [game.gameOver]);

  // Toast when a saved game is restored
  useEffect(() => {
    if (game.isRestored && !game.gameOver) {
      setTimeout(() => game.showToast?.('🔄 Game restored — pick up where you left off'), 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!puzzle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">⚽</div>
          <h2 className="font-display text-3xl text-accent mb-2">NO PUZZLE TODAY</h2>
          <p className="text-dimText">Check back tomorrow for a new grid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-16">

      {/* Header */}
      <header className="flex items-center justify-between py-5 border-b border-border">
        <div className="font-display text-3xl tracking-widest bg-gradient-to-r from-accent to-accent4 bg-clip-text text-transparent">
          Hat Trick Grid
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-surface2 border border-border rounded-full px-3 py-1.5 text-sm font-medium text-accent3">
            🔥 <span>{puzzle.streak || 0}</span>
          </div>
          <div className="font-mono text-xs text-dimText bg-surface2 border border-border rounded-full px-3 py-1.5">
            {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase()}
          </div>
        </div>
      </header>

      {/* Puzzle title + description */}
      {puzzle.title && (
        <div className="pt-4 pb-1">
          <h2 className="font-display text-2xl tracking-widest text-white leading-tight">
            {puzzle.title.toUpperCase()}
          </h2>
          {puzzle.description && (
            <p className="text-xs text-dimText mt-1 leading-relaxed">{puzzle.description}</p>
          )}
        </div>
      )}

      {/* Meta */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          <div className="text-sm text-dimText">
            Puzzle <span className="text-white font-semibold">#{puzzle.puzzle_number || puzzle.id}</span>
          </div>
          {showDifficulty && puzzle.difficulty && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border font-mono
              ${puzzle.difficulty === 'easy'   ? 'text-correct border-correct/30 bg-correct/10' :
                puzzle.difficulty === 'medium' ? 'text-hint border-hint/30 bg-hint/10' :
                'text-wrong border-wrong/30 bg-wrong/10'}`}
            >
              {puzzle.difficulty === 'easy' ? '🟢' : puzzle.difficulty === 'medium' ? '🟡' : '🔴'}{' '}
              {puzzle.difficulty.charAt(0).toUpperCase() + puzzle.difficulty.slice(1)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-dimText">
          <span>Guesses</span>
          <div className="flex gap-[3px] flex-wrap max-w-[180px]">
            {Array.from({ length: game.maxGuesses }).map((_, i) => {
              const log = game.guessLog[i];
              return (
                <div
                  key={i}
                  className={`w-[9px] h-[9px] rounded-full border transition-all ${
                    log === 'wrong' ? 'bg-wrong border-wrong shadow-[0_0_5px_var(--wrong)]'
                    : log === 'hint' ? 'bg-hint border-hint shadow-[0_0_5px_var(--hint)]'
                    : 'bg-surface3 border-borderB'
                  }`}
                />
              );
            })}
          </div>
        </div>
      </div>

      <ScoreBar
        score={game.score}
        correctCount={game.correctCount}
        elapsedSeconds={game.elapsedSeconds}
        gameOver={game.gameOver}
      />

      {/* View result button — shown when returning to a completed game */}
      {game.gameOver && !showModal && (
        <button
          onClick={() => setShowModal(true)}
          className="w-full mb-3 flex items-center justify-center gap-2 bg-surface2 border border-border rounded-xl px-4 py-2.5 text-sm font-semibold text-white hover:border-accent transition-colors"
        >
          📊 View result &amp; leaderboard
          {game.leaderboardSubmitted && (
            <span className="text-[10px] font-mono text-correct">✓ score saved</span>
          )}
        </button>
      )}

      {/* Controls row — hint + suggestions toggle */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={game.useHint}
          disabled={game.guessesLeft < game.hintCost || game.hintsUsed >= game.maxHints || game.gameOver}
          className="flex items-center gap-2 bg-surface2 border border-borderB rounded-full px-3.5 py-1.5 text-xs font-semibold text-hint cursor-pointer transition-all hover:bg-surface3 hover:border-hint disabled:opacity-35 disabled:cursor-default"
        >
          💡 Hint
          <span className="font-mono text-dimText text-[10px]">
            −{game.hintCost} · {game.maxHints - game.hintsUsed} left
          </span>
        </button>

        {/* Suggestions toggle — locked once game starts */}
        <button
          onClick={() => { game.toggleSuggestions(); trackSuggestionsToggled({ suggestionsOff: !game.suggestionsOff }); }}
          disabled={game.correctCount > 0 || game.guessLog.length > 0}
          title={game.correctCount > 0 || game.guessLog.length > 0 ? 'Cannot change after game starts' : ''}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all
            ${game.suggestionsOff
              ? 'bg-accent2/10 border-accent2 text-accent2'
              : 'bg-surface2 border-borderB text-dimText hover:border-accent hover:text-accent'
            }
            disabled:opacity-40 disabled:cursor-default`}
        >
          {game.suggestionsOff ? '🚫 No suggestions' : '💬 Suggestions on'}
          {game.suggestionsOff && (
            <span className="font-mono text-[10px] text-accent2">+{game.noSugBonus}pts</span>
          )}
        </button>
      </div>

      <Grid puzzle={puzzle} game={game} focusSearch={() => focusSearchRef.current?.()} />

      {/* Used players strip */}
      {game.usedPlayers.size > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center mt-2 mb-1 min-h-[28px]">
          <span className="text-[10px] font-mono text-[var(--text-dimmer)] mr-1">Used:</span>
          {[...game.usedPlayers].map(p => (
            <span key={p} className="text-[11px] font-medium px-2.5 py-0.5 bg-surface3 border border-borderB rounded-full text-dimText">
              {p.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
            </span>
          ))}
        </div>
      )}

      {/* Search box */}
      {game.activeCell && !game.gameOver && (
        <>
          <SearchBox
            puzzle={puzzle}
            activeCell={game.activeCell}
            usedPlayers={game.usedPlayers}
            onSubmit={game.submitAnswer}
            suggestionsOff={game.suggestionsOff}
            onReady={fn => { focusSearchRef.current = fn; }}
          />
          <button
            onClick={() => setShowSuggest(true)}
            className="w-full mt-1.5 text-center text-[11px] text-dimText hover:text-accent transition-colors py-1"
          >
            💬 Think we missed a valid answer?
          </button>
        </>
      )}

      {/* Result modal */}
      {showModal && (
        <ResultModal
          game={game}
          puzzle={puzzle}
          onClose={() => setShowModal(false)}
        />
      )}

      {showSuggest && (
        <SuggestAnswerModal
          puzzle={puzzle}
          activeCell={game.activeCell}
          onClose={() => setShowSuggest(false)}
        />
      )}

      <Toast message={game.toast} />
    </div>
  );
}
